// Helpers para o Azure File Share `dados` em devlnxstoragellm
// (montado em L:\ no Windows como SMB; aqui acessamos via REST do File Service).
//
// Layout:
//   <BASE_URL>/<filename>/current_<engine>.txt
//   <BASE_URL>/<filename>/speakers.json
//   <BASE_URL>/<filename>/principal/<filename>(_provider).txt   (auditoria)
//   <BASE_URL>/<filename>/backup/<filename>(_provider).txt
//
// Read é um GET simples (igual ao Blob).
// Write em Azure Files é em 2 etapas: PUT cria o arquivo com tamanho, PUT ?comp=range escreve os bytes.
//
// BASE_URL aponta para a pasta de transcricao (Data-Lake-LLM/infra/transcricoes).
// LAKE_BASE_URL aponta para a RAIZ do Lake (Data-Lake-LLM), usada p/ os seeds de
// report em infra/reports/<area>/<slug>/. O SAS (account SAS, ss=f srt=sco sp=rwdlc)
// vale para o share inteiro, entao serve para as duas bases.

const BASE_URL = process.env.REACT_APP_FILESHARE_BASE_URL;
const SAS = process.env.REACT_APP_TOKEN_FILES_LLM;

// Raiz do Lake: tudo ate (e incluindo) "Data-Lake-LLM".
export const LAKE_BASE_URL =
  process.env.REACT_APP_LAKE_BASE_URL ||
  (() => {
    const m = (BASE_URL || "").match(/^(.*\/Data-Lake-LLM)(\/|$)/i);
    return m ? m[1] : BASE_URL;
  })();

const _sas = () => (SAS?.startsWith("?") ? SAS : `?${SAS || ""}`);

export const fileShareUrl = (relPath, { cacheBust = false, base = BASE_URL } = {}) => {
  const sas = _sas();
  const t = cacheBust ? `&t=${Date.now()}` : "";
  return `${base}/${relPath}${sas}${t}`;
};

// Monta a URL com um parâmetro de query extra ANTES do SAS (ex.: restype=directory).
const fileShareUrlWith = (relPath, extraQuery, base = BASE_URL) => {
  const sas = _sas();
  const q = extraQuery ? `?${extraQuery}&${sas.slice(1)}` : sas;
  return `${base}/${relPath}${q}`;
};

export const getFileShareText = async (relPath, base = BASE_URL) => {
  const res = await fetch(fileShareUrl(relPath, { cacheBust: true, base }), {
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`GET ${relPath} → HTTP ${res.status}`);
  return res.text();
};

export const headFileShare = async (relPath, base = BASE_URL) => {
  const res = await fetch(fileShareUrl(relPath, { cacheBust: true, base }), {
    method: "HEAD",
  });
  if (!res.ok) throw new Error(`HEAD ${relPath} → HTTP ${res.status}`);
  return {
    lastModified: res.headers.get("Last-Modified"),
    contentLength: Number(res.headers.get("Content-Length") || 0),
  };
};

// Azure Files: PUT em 2 etapas.
// 1) Cria o arquivo (sem corpo) anunciando x-ms-content-length.
// 2) Escreve os bytes via PUT ?comp=range com x-ms-range e x-ms-write=update.
export const putFileShareText = async (
  relPath,
  text,
  contentType = "text/plain; charset=utf-8",
  base = BASE_URL
) => {
  const bytes = new TextEncoder().encode(text);
  const len = bytes.byteLength;
  const fileUrl = fileShareUrl(relPath, { base });

  const createRes = await fetch(fileUrl, {
    method: "PUT",
    headers: {
      "x-ms-type": "file",
      "x-ms-content-length": String(len),
      "x-ms-content-type": contentType,
    },
  });
  if (!createRes.ok) {
    throw new Error(`Create file ${relPath} → HTTP ${createRes.status}`);
  }

  if (len === 0) return;

  const sep = fileUrl.includes("?") ? "&" : "?";
  const rangeUrl = `${fileUrl}${sep}comp=range`;
  const writeRes = await fetch(rangeUrl, {
    method: "PUT",
    headers: {
      "x-ms-range": `bytes=0-${len - 1}`,
      "x-ms-write": "update",
      "Content-Type": "application/octet-stream",
    },
    body: bytes,
  });
  if (!writeRes.ok) {
    throw new Error(`Write range ${relPath} → HTTP ${writeRes.status}`);
  }
};

export const putFileShareJson = async (relPath, obj, base = BASE_URL) =>
  putFileShareText(
    relPath,
    JSON.stringify(obj, null, 2),
    "application/json; charset=utf-8",
    base
  );

// Azure Files não cria pastas-pai automaticamente ao gravar um arquivo.
// Cria cada segmento do caminho, de forma idempotente (409 = já existe).
export const ensureDirFileShare = async (dirRelPath, base = BASE_URL) => {
  const parts = dirRelPath.split("/").filter(Boolean);
  let acc = "";
  for (const part of parts) {
    acc = acc ? `${acc}/${part}` : part;
    const res = await fetch(fileShareUrlWith(acc, "restype=directory", base), {
      method: "PUT",
      headers: { "x-ms-type": "directory" },
    });
    if (!res.ok && res.status !== 409) {
      throw new Error(`Create dir ${acc} → HTTP ${res.status}`);
    }
  }
};

// Remove um arquivo do share. 404 é tratado como sucesso (idempotente).
export const deleteFileShare = async (relPath, base = BASE_URL) => {
  const res = await fetch(fileShareUrl(relPath, { base }), { method: "DELETE" });
  if (!res.ok && res.status !== 404) {
    throw new Error(`Delete ${relPath} → HTTP ${res.status}`);
  }
};
